import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';
import { logError } from '@/utils/errorHandler';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: undefined,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to Sentry with full context
    logError(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        errorInfo: {
          componentStack: errorInfo.componentStack,
          errorBoundary: this.constructor.name,
        },
        fallbackMessage: this.props.fallbackMessage,
      },
    });
  }

  // Function to attempt recovery (e.g., reload)
  private handleTryAgain = () => {
    this.setState({ hasError: false, error: undefined });
    // Depending on the error, you might want to reload the page
    // or try re-mounting the component differently.
    // For simplicity, we just reset state, allowing children to re-render.
    // A full page reload might be safer in many cases:
    // window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div
          className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border border-dashed border-danger-300 bg-danger-50 p-6 text-center"
          role="alert"
        >
          <AlertTriangle className="mb-4 size-12 text-danger-500" />
          <h2 className="mb-2 text-xl font-semibold text-danger-700">Something went wrong.</h2>
          <p className="mb-4 text-danger-600">
            {this.props.fallbackMessage ||
              'We encountered an unexpected error. Please try again or contact support if the problem persists.'}
          </p>
          {/* Optionally show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre className="mt-2 max-w-full overflow-auto rounded border bg-white p-2 text-left text-xs text-danger-700">
              {this.state.error.toString()}
              <br />
              {this.state.error.stack?.substring(0, 500)}...
            </pre>
          )}
          <Button variant="destructive" onClick={this.handleTryAgain} className="mt-6">
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
